import AutoModeIcon from '@mui/icons-material/AutoMode';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

export const FLOW_TRIGGER_MODE_DEFINITIONS = [
  {
    value: 'disabled',
    labelKey: 'workflow.automation_mode_disabled',
    labelFallback: 'Manual only',
    helpFallback: 'No automatic runs. Start this flow manually or from another flow.',
    tooltipFallback: 'No event, detail change, confirmation, interval, or calendar schedule starts this flow. Manual replay and the Trigger Flow action can still start it.'
  },
  {
    value: 'event',
    labelKey: 'workflow.automation_mode_event',
    labelFallback: 'New or updated event',
    helpFallback: 'Run when a document or event is processed.',
    tooltipFallback: 'Starts in the normal event-driven processing path when a document or event is processed. It does not wait for a detail-change filter or a schedule.'
  },
  {
    value: 'event_confirmed',
    labelKey: 'workflow.automation_mode_event_confirmed',
    labelFallback: 'When event is confirmed',
    helpFallback: 'Run only when an event changes from not confirmed to confirmed.',
    tooltipFallback: 'Starts only when an event transitions from not confirmed to confirmed. Unconfirming an event does not match this trigger.'
  },
  {
    value: 'event_change',
    labelKey: 'workflow.automation_mode_event_change',
    labelFallback: 'On event detail change',
    helpFallback: 'Run when an event detail matches the filters configured below.',
    tooltipFallback: 'Starts only when an explicit confirmation, tag, event date, or ingestion change matches every filter configured below. Empty filters accept any detailed change.'
  },
  {
    value: 'time',
    labelKey: 'workflow.automation_mode_time',
    labelFallback: 'Recurring interval',
    helpFallback: 'Run repeatedly every chosen number of minutes, hours, or days.',
    tooltipFallback: 'The scheduler starts this flow at the interval configured below. Event and document updates do not start it; actual start time follows the scheduler polling cycle.'
  },
  {
    value: 'calendar',
    labelKey: 'workflow.automation_mode_calendar',
    labelFallback: 'Calendar schedule',
    helpFallback: 'Run when the selected calendar fields match.',
    tooltipFallback: 'The scheduler starts this flow when all configured weekday, day-of-month, month, hour, and minute fields match in the saved time zone. Empty fields match any value.'
  }
];

export const normalizeFlowTriggerMode = (rawMode, fallbackAutoRunEnabled = null) => {
  const normalized = String(rawMode || '').trim().toLowerCase();
  if (['time', 'schedule', 'scheduled', 'auto'].includes(normalized)) return 'time';
  if (['calendar', 'cron', 'weekly', 'week', 'weekly schedule', 'weekly_schedule', 'weekly-schedule'].includes(normalized)) return 'calendar';
  if (['event', 'events', 'on_event', 'on event', 'change'].includes(normalized)) return 'event';
  if (['event_change', 'event change', 'event-change', 'timeevent_change', 'timeevent change', 'timeevent.changed'].includes(normalized)) return 'event_change';
  if (['event_confirmed', 'event confirmed', 'event-confirmed', 'confirmation', 'confirmed', 'on_confirm', 'on confirmation'].includes(normalized)) return 'event_confirmed';
  if (['disabled', 'disable', 'off', 'manual', 'none'].includes(normalized)) return 'disabled';
  return Number(fallbackAutoRunEnabled || 0) === 1 ? 'time' : 'event';
};

export const getTriggerModeOptions = (t) => FLOW_TRIGGER_MODE_DEFINITIONS.map((option) => ({
  ...option,
  label: t(option.labelKey, option.labelFallback),
  description: t(`workflow.automation_mode_help_${option.value}`, option.helpFallback),
  tooltip: t(`workflow.automation_mode_tooltip_${option.value}`, option.tooltipFallback),
}));

export const getFlowTriggerModeLabel = (rawMode, t, fallbackAutoRunEnabled = null) => {
  const normalizedMode = normalizeFlowTriggerMode(rawMode, fallbackAutoRunEnabled);
  const definition = FLOW_TRIGGER_MODE_DEFINITIONS.find((option) => option.value === normalizedMode)
    || FLOW_TRIGGER_MODE_DEFINITIONS[1];
  return t(definition.labelKey, definition.labelFallback);
};

/**
 * Shared visual language for flow triggers in the workflow canvas and dialogs.
 */
export const getTriggerVisualConfig = (triggerMode) => {
  if (triggerMode === 'time') {
    return {
      icon: AutoModeIcon,
      iconColor: '#2e7d32',
      borderColor: 'rgba(46, 125, 50, 0.42)',
      background: 'linear-gradient(135deg, #ffffff 0%, #f1f8e9 100%)',
      iconBackground: 'linear-gradient(135deg, rgba(76, 175, 80, 0.2) 0%, rgba(102, 187, 106, 0.1) 100%)',
      shadow: '0 6px 20px rgba(76, 175, 80, 0.22), inset 0 1px 2px rgba(255, 255, 255, 0.55)',
      hoverShadow: '0 10px 26px rgba(76, 175, 80, 0.3), 0 4px 10px rgba(76, 175, 80, 0.16)',
      labelColor: '#2e7d32',
      labelBackground: 'rgba(76, 175, 80, 0.1)',
      labelBorder: 'rgba(76, 175, 80, 0.26)'
    };
  }

  if (triggerMode === 'calendar') {
    return {
      icon: CalendarMonthIcon,
      iconColor: '#8a5b00',
      borderColor: 'rgba(138, 91, 0, 0.38)',
      background: 'linear-gradient(135deg, #ffffff 0%, #fff8e6 100%)',
      iconBackground: 'linear-gradient(135deg, rgba(255, 193, 7, 0.22) 0%, rgba(255, 224, 130, 0.12) 100%)',
      shadow: '0 6px 20px rgba(255, 193, 7, 0.18), inset 0 1px 2px rgba(255, 255, 255, 0.55)',
      hoverShadow: '0 10px 24px rgba(255, 193, 7, 0.26), 0 4px 10px rgba(255, 193, 7, 0.12)',
      labelColor: '#8a5b00',
      labelBackground: 'rgba(255, 193, 7, 0.12)',
      labelBorder: 'rgba(255, 193, 7, 0.26)'
    };
  }

  if (triggerMode === 'event_confirmed') {
    return {
      icon: CheckCircleOutlineIcon,
      iconColor: '#2e7d32',
      borderColor: 'rgba(46, 125, 50, 0.38)',
      background: 'linear-gradient(135deg, #ffffff 0%, #f3faf4 100%)',
      iconBackground: 'linear-gradient(135deg, rgba(46, 125, 50, 0.18) 0%, rgba(102, 187, 106, 0.09) 100%)',
      shadow: '0 6px 18px rgba(46, 125, 50, 0.18), inset 0 1px 2px rgba(255, 255, 255, 0.55)',
      hoverShadow: '0 10px 24px rgba(46, 125, 50, 0.26), 0 4px 10px rgba(46, 125, 50, 0.12)',
      labelColor: '#2e7d32',
      labelBackground: 'rgba(46, 125, 50, 0.09)',
      labelBorder: 'rgba(46, 125, 50, 0.24)'
    };
  }

  if (triggerMode === 'event_change') {
    return {
      icon: CheckCircleOutlineIcon,
      iconColor: '#6a1b9a',
      borderColor: 'rgba(106, 27, 154, 0.36)',
      background: 'linear-gradient(135deg, #ffffff 0%, #faf5ff 100%)',
      iconBackground: 'linear-gradient(135deg, rgba(156, 39, 176, 0.18) 0%, rgba(186, 104, 200, 0.09) 100%)',
      shadow: '0 6px 18px rgba(156, 39, 176, 0.18), inset 0 1px 2px rgba(255, 255, 255, 0.55)',
      hoverShadow: '0 10px 24px rgba(156, 39, 176, 0.26), 0 4px 10px rgba(156, 39, 176, 0.12)',
      labelColor: '#6a1b9a',
      labelBackground: 'rgba(156, 39, 176, 0.09)',
      labelBorder: 'rgba(156, 39, 176, 0.24)'
    };
  }

  if (triggerMode === 'disabled') {
    return {
      icon: PauseCircleOutlineIcon,
      iconColor: '#6d6d6d',
      borderColor: 'rgba(125, 125, 125, 0.35)',
      background: 'linear-gradient(135deg, #ffffff 0%, #f3f4f6 100%)',
      iconBackground: 'linear-gradient(135deg, rgba(117, 117, 117, 0.18) 0%, rgba(158, 158, 158, 0.08) 100%)',
      shadow: '0 3px 12px rgba(117, 117, 117, 0.16), inset 0 1px 2px rgba(255, 255, 255, 0.55)',
      hoverShadow: '0 8px 18px rgba(117, 117, 117, 0.24), 0 3px 8px rgba(117, 117, 117, 0.14)',
      labelColor: '#616161',
      labelBackground: 'rgba(117, 117, 117, 0.1)',
      labelBorder: 'rgba(117, 117, 117, 0.25)'
    };
  }

  return {
    icon: PlayArrowIcon,
    iconColor: '#1565c0',
    borderColor: 'rgba(21, 101, 192, 0.4)',
    background: 'linear-gradient(135deg, #ffffff 0%, #f1f7ff 100%)',
    iconBackground: 'linear-gradient(135deg, rgba(33, 150, 243, 0.2) 0%, rgba(100, 181, 246, 0.1) 100%)',
    shadow: '0 6px 20px rgba(33, 150, 243, 0.2), inset 0 1px 2px rgba(255, 255, 255, 0.55)',
    hoverShadow: '0 10px 24px rgba(33, 150, 243, 0.3), 0 4px 10px rgba(33, 150, 243, 0.14)',
    labelColor: '#1565c0',
    labelBackground: 'rgba(33, 150, 243, 0.1)',
    labelBorder: 'rgba(33, 150, 243, 0.24)'
  };
};
